/* RESPONSIBLE TEAM: team-standalone */

import { all, hash } from 'rsvp';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { get, post } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import type Store from '@ember-data/store';
import type SalesforcePlatform from 'embercom/objects/standalone/platforms/salesforce';
import type {
  SalesforceAdmin,
  SalesforceQueue,
  SalesforceSupportEmail,
} from 'embercom/objects/standalone/salesforce';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';

export default class SalesforceDataLoader {
  platform: SalesforcePlatform;
  private store!: Store;

  constructor(platform: SalesforcePlatform) {
    this.platform = platform;
    this.store = platform.store;
  }

  get app() {
    return this.platform.finStandaloneService.app;
  }

  get isLoadingSalesforceConfig() {
    return taskFor(this.loadSalesforceConfig).isRunning;
  }

  get isLoadingSalesforceImportOnlyConfig() {
    return taskFor(this.loadSalesforceImportOnlyConfig).isRunning;
  }

  get isLoadingSalesforceSetupData() {
    return taskFor(this.loadSalesforceSetupData).isRunning;
  }

  get isLoadingSalesforceInAppMessagingConfig() {
    return taskFor(this.loadSalesforceInAppMessagingConfig).isRunning;
  }

  get isLoadingSalesforceLiveChatConfig() {
    return taskFor(this.loadSalesforceLiveChatConfig).isRunning;
  }

  get isLoadingSalesforcePermissions() {
    return taskFor(this.checkSalesforcePermissions).isRunning;
  }

  get isLoadingSalesforcePermissionsHealthcheck() {
    return taskFor(this.loadSalesforcePermissionsHealthcheck).isRunning;
  }

  get isLoadingSalesforceCopilotInstallation() {
    return taskFor(this.checkSalesforceCopilotInstallationTask).isRunning;
  }

  // Helper to get the correct ID for API calls
  private getConfigId(config?: SalesforceConfiguration): string {
    if (this.app.canUseStandaloneMultiEnv && config?.id) {
      return config.id;
    }
    return this.app.id;
  }

  async performLoads() {
    let promises: Promise<unknown>[] = [
      taskFor(this.loadSalesforceConfig).perform(),
      taskFor(this.loadSalesforceImportOnlyConfig).perform(),
    ];

    await all(promises);

    // Load data for all configurations
    if (this.app.canUseStandaloneMultiEnv && this.platform.salesforceConfigs) {
      await all(
        this.platform.salesforceConfigs.map((config) => this.loadDataForConfiguration(config)),
      );
    } else if (this.platform.salesforceConfig) {
      await this.loadDataForConfiguration(this.platform.salesforceConfig);
    }

    await this.platform.loadDataFields();
  }

  async performLazyLoads() {
    this.app.loadEmailDomains(false);
  }

  // Load all data for a specific configuration
  private async loadDataForConfiguration(config: SalesforceConfiguration) {
    let configId = this.getConfigId(config);

    await all([
      this.loadSalesforceSetupDataForConfig(config, configId),
      this.loadSalesforceInAppMessagingConfigForConfig(config),
      this.loadSalesforceLiveChatConfigForConfig(config, configId),
    ]);
  }

  // Load setup data for a specific configuration
  private async loadSalesforceSetupDataForConfig(
    config: SalesforceConfiguration,
    configId: string,
  ): Promise<void> {
    if (config.isPendingSetup) {
      let defaultData = this.platform.createDefaultConfigData();
      this.platform.setConfigurationData(config.id, defaultData);
      return;
    }

    let promises = {
      salesforceAdmins: get(
        `/ember/standalone/salesforce_configurations/${configId}/list_salesforce_users`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      salesforceQueues: get(
        `/ember/standalone/salesforce_configurations/${configId}/list_salesforce_queues`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      salesforceCaseOrigins: get(
        `/ember/standalone/salesforce_configurations/${configId}/list_salesforce_case_origins`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      selectedCaseOrigins: get(
        `/ember/standalone/salesforce_configurations/${configId}/list_selected_case_origins`,
        {
          app_id: this.app.id,
        },
      ),
      salesforceSupportEmails: get(
        `/ember/standalone/salesforce_configurations/${configId}/list_support_emails`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      checkPermissions: get(
        `/ember/standalone/salesforce_configurations/${configId}/check_permissions`,
        {
          app_id: this.app.id,
        },
      ).catch(() => ({})),
      permissionsHealthcheck: get('/ember/standalone/healthcheck', {
        app_id: this.app.id,
      }).catch(() => []),
      checkSalesforceCopilotInstallation:
        this.checkSalesforceCopilotInstallationForConfig(configId),
    };

    let {
      salesforceAdmins,
      salesforceQueues,
      salesforceCaseOrigins,
      selectedCaseOrigins,
      salesforceSupportEmails,
      checkPermissions,
      permissionsHealthcheck,
      checkSalesforceCopilotInstallation,
    } = await hash(promises);

    this.platform.setConfigurationData(config.id, {
      setupData: {
        admins: salesforceAdmins as Array<SalesforceAdmin>,
        queues: salesforceQueues as Array<SalesforceQueue>,
        caseOrigins: salesforceCaseOrigins.sort() as Array<
          string | { value: string; label: string }
        >,
        supportEmails: salesforceSupportEmails as Array<SalesforceSupportEmail>,
        permissionsHealthcheck,
        hasPermissionSet: checkPermissions.has_permission_set,
        hasPermissionSetAssignedToInstaller:
          checkPermissions.has_permission_set_assigned_to_installer,
        hasDigitalExperiences: checkPermissions.has_digital_experiences,
        copilot: {
          isInstalled: checkSalesforceCopilotInstallation.has_copilot_installed,
          hasCustomSettingsFields: checkSalesforceCopilotInstallation.has_custom_settings_fields,
          installedOnLightningPages:
            checkSalesforceCopilotInstallation.installed_on_lightning_pages,
          unableToDetectLightningPageInstallation:
            checkSalesforceCopilotInstallation.unable_to_detect_lightning_page_installation,
          lightningWebSecurityEnabled: checkSalesforceCopilotInstallation.lws_enabled,
        },
      },
      selectedCaseOrigins,
    });
  }

  // Load in-app messaging config for a specific configuration
  private async loadSalesforceInAppMessagingConfigForConfig(
    config: SalesforceConfiguration,
  ): Promise<void> {
    if (config.isPendingSetup) {
      return;
    }

    try {
      let inAppConfig;
      if (this.app.canUseStandaloneMultiEnv && config.organisationId) {
        let results = await this.store.query(
          'standalone/salesforce-in-app-messaging-configuration',
          {
            organizationId: config.organisationId.substring(0, 15),
          },
        );
        inAppConfig = results.firstObject;
      } else {
        inAppConfig = await this.store.findRecord(
          'standalone/salesforce-in-app-messaging-configuration',
          this.app.id,
        );
      }
      if (inAppConfig) {
        this.platform.setConfigurationData(config.id, { inAppMessagingConfig: inAppConfig });
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  // Load live chat config for a specific configuration
  private async loadSalesforceLiveChatConfigForConfig(
    config: SalesforceConfiguration,
    configId: string,
  ): Promise<void> {
    if (!this.app.canUseHandoffToSalesforceChat || config.isPendingSetup) {
      return;
    }

    try {
      let liveChatConfig = await this.store.findRecord(
        'standalone/salesforce-live-chat-configuration',
        configId,
      );
      this.platform.setConfigurationData(config.id, { liveChatConfig });
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  // Helper for copilot installation check for a specific configuration
  private checkSalesforceCopilotInstallationForConfig(configId: string): Promise<{
    has_copilot_installed: boolean;
    has_custom_settings_fields: boolean;
    installed_on_lightning_pages: boolean;
    unable_to_detect_lightning_page_installation: boolean;
    lws_enabled: boolean;
  }> {
    return get(
      `/ember/standalone/salesforce_configurations/${configId}/check_copilot_installation`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({
      has_copilot_installed: false,
      has_custom_settings_fields: false,
      installed_on_lightning_pages: false,
      unable_to_detect_lightning_page_installation: false,
      lws_enabled: false,
    }));
  }

  @dropTask *loadSalesforceConfig() {
    try {
      if (this.app.canUseStandaloneMultiEnv) {
        this.platform.salesforceConfigs = yield this.store.findAll(
          'standalone/salesforce-configuration',
        );
        this.platform.salesforceConfig = this.platform.salesforceConfigs?.firstObject;

        if (!this.platform.salesforceConfig) {
          this.platform.salesforceConfig = this.store.createRecord(
            'standalone/salesforce-configuration',
          );
        }
      } else {
        this.platform.salesforceConfig = yield this.store.findRecord(
          'standalone/salesforce-configuration',
          this.app.id,
        );

        if (!this.platform.salesforceConfig) {
          this.platform.salesforceConfig = this.store.createRecord(
            'standalone/salesforce-configuration',
            {
              id: this.app.id,
            },
          );
        }
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceImportOnlyConfig(): TaskGenerator<void> {
    // TODO Replace this with usage of Ember Data?
    let salesforceImportOnlyConfig = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/find_import_only_integration`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({}));

    if (salesforceImportOnlyConfig?.instance_url) {
      this.platform.salesforceImportOnlyConfig = {
        instanceUrl: salesforceImportOnlyConfig.instance_url,
        organizationId: salesforceImportOnlyConfig.organization_id,
        isSandbox: salesforceImportOnlyConfig.is_sandbox,
      };
    }
  }

  @dropTask *loadSalesforceSetupData(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    yield this.loadSalesforceSetupDataForConfig(this.platform.salesforceConfig, configId);
  }

  @dropTask *loadSalesforceInAppMessagingConfig(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig) {
      return;
    }

    yield this.loadSalesforceInAppMessagingConfigForConfig(this.platform.salesforceConfig);
  }

  @dropTask *loadSalesforceSupportEmails(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig || this.platform.salesforceConfig.isPendingSetup) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    let supportEmails = (yield get(
      `/ember/standalone/salesforce_configurations/${configId}/list_support_emails`,
      {
        app_id: this.app.id,
      },
    ).catch(() => [])) as Array<SalesforceSupportEmail>;

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      supportEmails,
    };
  }

  @dropTask *loadSalesforceLiveChatConfig(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    yield this.loadSalesforceLiveChatConfigForConfig(this.platform.salesforceConfig, configId);
  }

  @dropTask *loadSalesforcePermissionsHealthcheck(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig || this.platform.salesforceConfig.isPendingSetup) {
      return;
    }

    let permissionsHealthcheck = yield get('/ember/standalone/healthcheck', {
      app_id: this.app.id,
    }).catch(() => []);

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      permissionsHealthcheck,
    };
  }

  @restartableTask *checkSalesforcePermissions(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    let { has_permission_set, has_permission_set_assigned_to_installer, has_digital_experiences } =
      yield get(`/ember/standalone/salesforce_configurations/${configId}/check_permissions`, {
        app_id: this.app.id,
      }).catch(() => ({
        has_permission_set: this.platform.salesforceSetupData.hasPermissionSet,
        has_permission_set_assigned_to_installer:
          this.platform.salesforceSetupData.hasPermissionSetAssignedToInstaller,
        has_digital_experiences: this.platform.salesforceSetupData.hasDigitalExperiences,
      }));

    if (has_permission_set_assigned_to_installer) {
      // Reload the setup data as we've just assigned the permission set to the installer
      yield taskFor(this.loadSalesforceSetupData).perform();
    } else {
      // Otherwise, just update the setup data with the permission set status
      this.platform.salesforceSetupData = {
        ...this.platform.salesforceSetupData,
        hasPermissionSet: has_permission_set,
        hasPermissionSetAssignedToInstaller: has_permission_set_assigned_to_installer,
        hasDigitalExperiences: has_digital_experiences,
      };
    }
  }

  @restartableTask *hasRequiredSalesforceFinPermissionsTask(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig || this.platform.salesforceConfig.isPendingSetup) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    let hasPermissions = yield get(
      `/ember/standalone/salesforce_configurations/${configId}/has_required_fin_permissions`,
      {
        app_id: this.app.id,
      },
    );
    this.platform.hasPermissionPackageBeenInstalled = hasPermissions.has_fin_permissions;
    this.platform.hasRequiredSalesforceFinPermissions = hasPermissions.has_admin_permission_set;
  }

  @restartableTask *checkSalesforceCopilotInstallationTask(): TaskGenerator<void> {
    if (!this.platform.salesforceConfig) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    let {
      has_copilot_installed,
      has_custom_settings_fields,
      installed_on_lightning_pages,
      unable_to_detect_lightning_page_installation,
      lws_enabled,
    } = yield this.checkSalesforceCopilotInstallationForConfig(configId);

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      copilot: {
        ...this.platform.salesforceSetupData.copilot,
        isInstalled: has_copilot_installed,
        hasCustomSettingsFields: has_custom_settings_fields,
        installedOnLightningPages: installed_on_lightning_pages,
        unableToDetectLightningPageInstallation: unable_to_detect_lightning_page_installation,
        lightningWebSecurityEnabled: lws_enabled,
      },
    };
  }

  @dropTask *createCopilotCustomSettingsFieldsTask() {
    if (!this.platform.salesforceConfig) {
      return;
    }

    let configId = this.getConfigId(this.platform.salesforceConfig);
    yield post(
      `/ember/standalone/salesforce_configurations/${configId}/create_copilot_custom_settings_fields`,
    );

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      copilot: {
        ...this.platform.salesforceSetupData.copilot,
        hasCustomSettingsFields: true,
      },
    };
  }
}
